/**
 * FileTypeIcon — renders the design-specified file-type icon for BIM/CAD file
 * nodes in the object tree, as defined in BM-14272.
 *
 * Each icon is a 20×20 SVG supplied by design (stored in src/media/icons/).
 * They are rendered at a requested size via CSS transform to stay sharp at any scale.
 */

import DgnIcon from "media/icons/file-type-dgn.svg?react";
import DwgIcon from "media/icons/file-type-dwg.svg?react";
import IfcIcon from "media/icons/file-type-ifc.svg?react";
import NavisIcon from "media/icons/file-type-navis.svg?react";
import OtherIcon from "media/icons/file-type-other.svg?react";
import PdfIcon from "media/icons/file-type-pdf.svg?react";
import PointcloudIcon from "media/icons/file-type-pointcloud.svg?react";
import RvtIcon from "media/icons/file-type-rvt.svg?react";
import TerrainIcon from "media/icons/file-type-terrain.svg?react";

type FileTypeIconProps = {
    /** Lowercase file extension extracted from the file node path. */
    ext: string;
    /** Rendered size in pixels (applied to both width and height). */
    size: number;
    style?: React.CSSProperties;
};

const FILE_TYPE_ICONS = {
    navis: NavisIcon,
    rvt: RvtIcon,
    dgn: DgnIcon,
    dwg: DwgIcon,
    ifc: IfcIcon,
    pointcloud: PointcloudIcon,
    pdf: PdfIcon,
    terrain: TerrainIcon,
    other: OtherIcon,
} as const;

type FileTypeIconName = keyof typeof FILE_TYPE_ICONS;

function fileTypeForExtension(ext: string): FileTypeIconName {
    switch (ext) {
        case "nwd":
        case "nwc":
        case "nwf":
            return "navis";
        case "rvt":
            return "rvt";
        case "dgn":
            return "dgn";
        case "dwg":
            return "dwg";
        case "ifc":
            return "ifc";
        case "e57":
        case "las":
        case "laz":
        case "pts":
        case "ptx":
        case "rcs":
            return "pointcloud";
        case "pdf":
            return "pdf";
        case "dem":
        case "tif":
        case "tiff":
            return "terrain";
        default:
            return "other";
    }
}

/**
 * Returns the colour-coded file-type icon for the given extension.
 * Falls back to the generic "other" icon for any unrecognised extension.
 */
export function FileTypeIcon({ ext, size, style }: FileTypeIconProps) {
    const fileType = fileTypeForExtension(ext);
    const Icon = FILE_TYPE_ICONS[fileType];

    return (
        <Icon
            aria-hidden="true"
            data-testid="file-type-icon"
            data-file-type={fileType}
            style={{
                flexShrink: 0,
                width: size,
                height: size,
                ...style,
            }}
        />
    );
}
